import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRpc } from '../../tracking/useRpc';
import { formatClock, formatDateRange, formatPct, formatSgdCompact, periodRange, share, singaporeToday, type DrillSpec, type PeriodKey, type Speed } from './ownerData';
import { FEW_QUOTES, teamRows, teamSpeedDrill, teamTiles, unattributedDrill, unattributedSentence, type OwnerTeam, type TeamRow } from './teamData';
import { DrillLink, LoadError, PeriodSelect, StatTile } from './parts';
import { INK, SERIES } from '../chartTheme';
import RecordsDialog from './RecordsDialog';

const nf = (n: number | null | undefined) => (n == null ? '-' : Number(n).toLocaleString('en-SG'));

/** Median with its n; a small sample is greyed and says why. */
const SpeedCell: React.FC<{ speed: Speed; few: boolean }> = ({ speed, few }) =>
  speed.answered === 0 ? (
    <Tooltip title="No quotes for RFQs that arrived Mon-Fri 09:00-18:00">
      <span>-</span>
    </Tooltip>
  ) : (
    <Tooltip title={`${nf(speed.answered)} quotes for RFQs that arrived in office hours${few ? ' - too few to compare fairly' : ''}`}>
      <Box component="span" sx={{ color: few ? INK.muted : undefined, whiteSpace: 'nowrap' }}>
        {formatClock(speed.median_minutes)}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          (n={nf(speed.answered)})
        </Typography>
      </Box>
    </Tooltip>
  );

/**
 * A figure in a row that opens its own records. The row itself opens the
 * person's quotes, so the click stops here instead of reaching the row too.
 */
const CellDrill: React.FC<{ spec: DrillSpec | null; onDrill: (d: DrillSpec) => void; children: React.ReactNode }> = ({ spec, onDrill, children }) =>
  spec ? (
    <Box component="span" onClick={(e) => e.stopPropagation()}>
      <DrillLink align="right" spec={spec} onDrill={onDrill}>
        {children}
      </DrillLink>
    </Box>
  ) : (
    <>{children}</>
  );

/** A share bar behind a count, so the split between people reads at a glance. */
const ShareBar: React.FC<{ pct: number | null }> = ({ pct }) => (
  <Box sx={{ height: 6, bgcolor: '#f0efec', borderRadius: '3px', overflow: 'hidden', mt: 0.5 }} aria-hidden>
    <Box sx={{ width: `${pct ?? 0}%`, height: '100%', bgcolor: SERIES.system, borderRadius: '3px' }} />
  </Box>
);

const Name: React.FC<{ row: TeamRow }> = ({ row }) => (
  <>
    <Typography variant="body2" sx={{ fontWeight: 600, color: row.notRecorded ? 'text.secondary' : undefined }}>
      {row.name}
    </Typography>
    {row.caption && (
      <Typography variant="caption" color="text.secondary" display="block">
        {row.caption}
      </Typography>
    )}
  </>
);

const TeamTable: React.FC<{ rows: TeamRow[]; team: OwnerTeam; onDrill: (d: DrillSpec) => void }> = ({ rows, team, onDrill }) => {
  const t = team.totals;
  const allQuotes: DrillSpec = { title: 'Quotes sent through the system', kind: 'system_quotes', from: team.range.from, to: team.range.to };
  const allPricedLines: DrillSpec = {
    title: 'Priced lines on quotes sent',
    kind: 'pricing_lines',
    from: team.range.from,
    to: team.range.to,
    note: 'Each line sent with a system suggestion. Within tolerance = the price sent is within max(2%, a minimum by price band) of the suggestion.',
  };
  return (
    <TableContainer>
      <Table size="small" aria-label="Quotes sent through the system, by salesperson">
        <TableHead>
          <TableRow>
            <TableCell>Salesperson</TableCell>
            <TableCell align="right">Quotes sent</TableCell>
            <TableCell align="right">Lines priced</TableCell>
            <TableCell align="right">
              <Tooltip title="Price sent x quantity on their quotes - what was quoted, not what was ordered">
                <span>Quoted value</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Median time to quote</TableCell>
            <TableCell align="right">Within 1 h</TableCell>
            <TableCell align="right">Within 4 h</TableCell>
            <TableCell align="right">
              <Tooltip title="Of their lines that had a system suggestion, the share sent within tolerance of it">
                <span>At the system’s price</span>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            // The whole row opens the person's quotes. The name is the keyboard-reachable link;
            // its click (mouse or Enter) bubbles to the row, so it does not open the list itself.
            <TableRow key={r.key} hover onClick={() => onDrill(r.drill)} sx={{ cursor: 'pointer' }}>
              <TableCell>
                <DrillLink spec={r.drill} onDrill={() => undefined}>
                  <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                    {r.name}
                  </Typography>
                </DrillLink>
                {r.caption && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.caption}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 110 }}>
                {nf(r.quotes)}
                <Typography variant="caption" color="text.secondary" display="block">
                  {r.quoteShareText} of all
                </Typography>
                <ShareBar pct={r.quoteShare} />
              </TableCell>
              <TableCell align="right">{nf(r.linesPriced)}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {formatSgdCompact(r.quotedValue)}
              </TableCell>
              <TableCell align="right">
                <CellDrill spec={r.speedDrill} onDrill={onDrill}>
                  <SpeedCell speed={r.speed} few={r.fewQuotes} />
                </CellDrill>
              </TableCell>
              <TableCell align="right" sx={{ color: r.fewQuotes ? INK.muted : undefined }}>
                {formatPct(r.speed.pct_within_1h)}
              </TableCell>
              <TableCell align="right" sx={{ color: r.fewQuotes ? INK.muted : undefined }}>
                {formatPct(r.speed.pct_within_4h)}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <CellDrill spec={r.atSystemDrill} onDrill={onDrill}>
                  <Tooltip title={`${nf(r.atSystem)} of ${nf(r.withSuggestion)} lines with a system suggestion`}>
                    <span>
                      {formatPct(r.atSystemPct)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({nf(r.atSystem)}/{nf(r.withSuggestion)})
                      </Typography>
                    </span>
                  </Tooltip>
                </CellDrill>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ '& td': { fontWeight: 600, color: 'text.primary', fontSize: 14, borderBottom: 'none' } }}>
            <TableCell>Everyone</TableCell>
            <TableCell align="right">
              <DrillLink align="right" spec={allQuotes} onDrill={onDrill}>
                {nf(t.quotes_sent)}
              </DrillLink>
            </TableCell>
            <TableCell align="right">{nf(t.lines_priced)}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {formatSgdCompact(t.quoted_value)}
            </TableCell>
            <TableCell align="right">
              <CellDrill spec={t.speed.answered ? teamSpeedDrill(team) : null} onDrill={onDrill}>
                <SpeedCell speed={t.speed} few={false} />
              </CellDrill>
            </TableCell>
            <TableCell align="right">{formatPct(t.speed.pct_within_1h)}</TableCell>
            <TableCell align="right">{formatPct(t.speed.pct_within_4h)}</TableCell>
            <TableCell align="right">
              <CellDrill spec={t.lines_with_suggestion ? allPricedLines : null} onDrill={onDrill}>
                {formatPct(share(t.lines_at_system_price, t.lines_with_suggestion))}
              </CellDrill>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

/**
 * Phone: one card per person with every figure labelled, instead of an
 * eight-column table. The top of the card opens their quotes; the median and
 * the system-price share open the records behind them.
 */
const TeamCards: React.FC<{ rows: TeamRow[]; onDrill: (d: DrillSpec) => void }> = ({ rows, onDrill }) => (
  <Stack spacing={1}>
    {rows.map((r) => {
      const figures: Array<{ label: string; value: string; drill?: DrillSpec | null }> = [
        { label: 'Quoted value', value: formatSgdCompact(r.quotedValue) },
        { label: 'Lines priced', value: nf(r.linesPriced) },
        { label: 'Median time to quote', value: r.speed.answered ? `${formatClock(r.speed.median_minutes)} (n=${nf(r.speed.answered)})` : '-', drill: r.speedDrill },
        { label: 'Within 1 h / 4 h', value: r.speed.answered ? `${formatPct(r.speed.pct_within_1h)} / ${formatPct(r.speed.pct_within_4h)}` : '-' },
        { label: 'At the system’s price', value: r.withSuggestion ? `${formatPct(r.atSystemPct)} (${nf(r.atSystem)}/${nf(r.withSuggestion)})` : '-', drill: r.atSystemDrill },
      ];
      return (
        <Paper key={r.key} variant="outlined" sx={{ overflow: 'hidden' }}>
          <ButtonBase
            onClick={() => onDrill(r.drill)}
            aria-label={`${r.name}${r.caption ? ` (${r.caption})` : ''}: ${r.quotes} quotes. Open their quotes`}
            sx={{ display: 'block', width: '100%', textAlign: 'left', p: 1.5, pb: 1, '&:focus-visible': { outline: `2px solid ${SERIES.system}`, outlineOffset: -2 } }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Name row={r} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'primary.main' }}>
                {nf(r.quotes)} {r.quotes === 1 ? 'quote' : 'quotes'} ›
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {r.quoteShareText} of all quotes sent through the system
            </Typography>
            <ShareBar pct={r.quoteShare} />
          </ButtonBase>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, px: 1.5, pb: 1.5 }}>
            {figures.map((f) => (
              <Box key={f.label}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {f.label}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: r.fewQuotes && f.label.startsWith('Median') ? INK.muted : undefined }}>
                  {f.drill ? (
                    <DrillLink spec={f.drill} onDrill={onDrill}>
                      {f.value}
                    </DrillLink>
                  ) : (
                    f.value
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      );
    })}
  </Stack>
);

/**
 * Analytics "Team": who sent the quotes that went out through the system, how
 * quickly, and how often at the system's price. Replies sent by hand from
 * Outlook carry no sender, so they are counted, not attributed.
 */
const TeamTab: React.FC<{ period: PeriodKey; onPeriodChange: (p: PeriodKey) => void }> = ({ period, onPeriodChange }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('md'));
  const today = singaporeToday();
  const range = useMemo(() => periodRange(period, today), [period, today]);
  const [drill, setDrill] = useState<DrillSpec | null>(null);
  // On failure the last loaded period stays on screen, labelled with its own dates.
  const { data: team, error: rpcError, loading, reload: retry } = useRpc<OwnerTeam>('get_owner_team', { p_from: range.from, p_to: range.to });
  // A failure is shown once no newer call is in flight.
  const error = loading ? null : rpcError;
  const rows = useMemo(() => (team ? teamRows(team) : []), [team]);
  const tiles = useMemo(() => (team ? teamTiles(team) : []), [team]);

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, mb: 2 }}>
        Who sent the quotes that went out through the system, how quickly, and how often at the system’s price. Replies sent by hand from Outlook
        do not record who sent them, so they are counted separately. Every number opens the quotes it was counted from.
      </Typography>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 3, display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1, alignItems: 'center' }}>
        <PeriodSelect value={period} onChange={onPeriodChange} id="team-period" />
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {loading ? 'Loading…' : team ? `Showing ${formatDateRange(team.range.from, team.range.to)}` : ''}
        </Typography>
        {loading && <CircularProgress size={18} aria-label="Loading the team figures" />}
      </Paper>

      {error && (
        <Box sx={{ mb: 2 }}>
          <LoadError what="the team figures" message={error} onRetry={retry} severity={team ? 'warning' : 'error'}>
            {team ? `Still showing ${formatDateRange(team.range.from, team.range.to)}.` : null}
          </LoadError>
        </Box>
      )}

      {!team && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
          <CircularProgress aria-label="Loading the team figures" />
        </Box>
      )}

      {team && (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            {tiles.map((t) => (
              <StatTile key={t.id} tile={t} onDrill={setDrill} />
            ))}
          </Box>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              By salesperson
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 900 }}>
              Quotes sent through the system in the period, by who sent them. Time to quote is clock time from the RFQ reaching the system to the quote
              going out, for RFQs that arrived Mon-Fri 09:00-18:00; medians on fewer than {FEW_QUOTES} quotes are greyed. It is not Business overview’s
              “time to first reply”, which starts when the email arrives and includes replies sent by hand. Select a person for their quotes, or a median or
              system-price figure for the records behind it.
            </Typography>
            {rows.length === 0 ? (
              <Typography color="text.secondary">No quotes were sent through the system in this period.</Typography>
            ) : phone ? (
              <TeamCards rows={rows} onDrill={setDrill} />
            ) : (
              <TeamTable rows={rows} team={team} onDrill={setDrill} />
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
              Replies no one can be named for
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 900 }}>
              {unattributedSentence(team)}
            </Typography>
            {team.unattributed.manual_replies > 0 && (
              <Box sx={{ mt: 1 }}>
                <DrillLink spec={unattributedDrill(team)} onDrill={setDrill}>
                  See the {nf(team.unattributed.manual_replies)} RFQs answered by hand
                </DrillLink>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Counted from the sales mailbox by the date the RFQ arrived
              {team.mailbox_scanned_through
                ? `; the mailbox was last read ${new Date(team.mailbox_scanned_through).toLocaleString('en-SG', { timeZone: 'Asia/Singapore', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}, so later replies are not in yet`
                : ''}
              . Why this differs from the quote count above: the table counts every quote sent through the system, by the day it was sent; the mailbox
              counts RFQ email threads by the day they arrived, as sent through the system only when the system sent the first reply. Quotes for RFQs
              that never came in as an email thread in this mailbox (portal RFQs, for example) or that followed a reply sent by hand are in the table only.
            </Typography>
          </Paper>
        </Box>
      )}

      <RecordsDialog spec={drill} onClose={() => setDrill(null)} />
    </Box>
  );
};

export default TeamTab;
